import type { Href } from "expo-router";
import type { AppRole } from "../../../../lib/auth";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { setUserRole } from "../../../../lib/admin";
import { getStudentHistory, getStudentProfile } from "../../../../lib/studentProfile";
import type { StudentCompetitionHistoryRow, StudentProfileFull } from "../../../../types/database";
import { SPACING } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../../components/admin/ui";
import { SectionHeader } from "../../../../components/aspects/SectionHeader";

const THEME = PANE_THEMES.Admin;

const ROLE_OPTIONS: { label: string; value: AppRole | null }[] = [
  { label: "No role", value: null },
  { label: "Student", value: "student" },
  { label: "Client", value: "client" },
  { label: "Admin licensing", value: "admin_licensing" },
  { label: "Admin RM", value: "admin_rm" },
  { label: "Super admin", value: "super_admin" },
];

export default function AdminStudentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [profile, setProfile] = useState<StudentProfileFull | null>(null);
  const [history, setHistory] = useState<StudentCompetitionHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<AppRole | null>("student");
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (typeof id !== "string") return;
    setLoading(true);
    setErr(null);
    try {
      const [p, h] = await Promise.all([getStudentProfile(id), getStudentHistory(id)]);
      setProfile(p);
      setHistory(
        [...h].sort((a, b) => {
          const ta = a.closes_at ? new Date(a.closes_at).getTime() : 0;
          const tb = b.closes_at ? new Date(b.closes_at).getTime() : 0;
          return tb - ta;
        })
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const schoolLine = useMemo(() => {
    if (!profile) return "";
    const parts: string[] = [];
    if (profile.university) parts.push(profile.university);
    if (profile.major) parts.push(profile.major);
    if (profile.graduation_year != null) parts.push(String(profile.graduation_year));
    return parts.join(" · ");
  }, [profile]);

  async function onSaveRole() {
    if (typeof id !== "string") return;
    setSavedNote(null);
    try {
      await setUserRole(id, selectedRole);
      setSavedNote("Role updated.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save role");
    }
  }

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading student…" />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 880 }}>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/admin/students" as Href);
          }}
          style={[
            { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 6 } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, letterSpacing: 1.2, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
            ← Back
          </Text>
        </Pressable>

        <AdminPageHeader
          theme={THEME}
          eyebrow="People · Student"
          title={profile?.full_name ?? "Student"}
          subtitle={schoolLine || undefined}
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}
        {savedNote ? <View style={{ marginBottom: SPACING.md }}><AdminBanner theme={THEME} tone="success" message={savedNote} /></View> : null}

        {/* Change role */}
        <SectionHeader theme={THEME} title="Change role" tone="dark" />
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {ROLE_OPTIONS.map((opt) => (
            <FilterChip key={opt.label} theme={THEME} label={opt.label} active={selectedRole === opt.value} onPress={() => setSelectedRole(opt.value)} />
          ))}
        </View>
        <View style={{ marginTop: 12 }}>
          <AdminButton theme={THEME} label="Save role" onPress={() => void onSaveRole()} />
        </View>

        {/* Competition history */}
        <View style={{ marginTop: SPACING.xl }}>
          <SectionHeader theme={THEME} title="Competition history" tone="dark" />
          {history.length === 0 ? (
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, color: THEME.textOnSurfaceDim }}>
              No completed competitions yet.
            </Text>
          ) : (
            <ListRowGroup theme={THEME}>
              {history.map((row, i) => (
                <ListRow
                  key={row.competition_id}
                  theme={THEME}
                  first={i === 0}
                  title={row.competition_name}
                  subtitle={
                    <View>
                      <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>
                        {row.company_name}
                      </Text>
                      <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
                        Submitted {row.submitted_count} of {row.total_deliverables} · {row.placement != null ? `Placed #${row.placement}` : "Pending"}
                      </Text>
                    </View>
                  }
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
